import React, {useState, useRef, useEffect} from 'react';
import b_ from 'b_';

import {Button, Icon, Tumbler} from 'client/common/components/lego';
import ShareButton from 'client/common/components/share-button2';
import ModeButton from 'client/common/components/mode-button';
import {useMobileMessage} from 'client/common/components/mobile-message';

import {useSurveySlug} from 'client/common/contexts/slug';

import {Device} from 'client/common/utils/device-width';
import cfg from 'client/common/utils/client-config';

import I18N from 'assets/i18n/brandlift-project';

import {useDevice} from 'client/common/hooks/device';
import {useSurveySettings} from 'client/common/hooks/survey';

import PhoneIcon from './icons/phone.svg';
import TabletIcon from './icons/tablet.svg';
import PanelIcon from './icons/panel.svg';

import {PreviewDevice} from './types';

import {PREVIEW_DEVICES} from './consts';

import './preview.css';

const i18n = I18N.common.preview;
const b = b_.with('preview2');

const DEVICE_ICONS = {
    mobile: PhoneIcon,
    desktop: TabletIcon,
    panel: PanelIcon
};

const DISPLAY_HIDDEN_QUESTIONS_TYPE = 'survey.embed.display_hidden_questions';
const SET_THEME_MODE_EVENT= 'survey.embed.set_theme_mode';

export function getPreviewLink(slug: string, device: PreviewDevice): string {
    return device === 'panel'
        ? `${cfg.previewHost}/poll/${slug}`
        : `${cfg.previewHost}/poll/video/${slug}?_theme_=video`;
}

interface PreviewProps {
    className?: string;
    title?: string;
    onlyPanel?: boolean;
    withHeader?: boolean;
    onClose?: () => void;
    data?: {pageId?: number};
}

// eslint-disable-next-line max-statements, complexity
export default function Preview({
    className = '',
    onClose,
    onlyPanel = false,
    withHeader = true,
    data
}: PreviewProps) {
    const slug = useSurveySlug();
    const supportDarkMode = useSurveySettings()?.supportDarkMode;
    const [selectedDevice, setSelectedDevice] = useState<PreviewDevice>(
        onlyPanel ? 'panel' : 'desktop'
    );
    const [isFrameLoading, setIsFrameLoading] = useState(true);
    const [isFrameTumblerOn, setIsFrameTumblerOn] = useState(false);
    const [isDarkModeTumblerOn, setIsDarkModeTumblerOn] = useState(false);
    const iframe = useRef<HTMLIFrameElement>(null);
    const [showMobileMessage] = useMobileMessage();

    const device = useDevice();

    const previewURL = getPreviewLink(slug, selectedDevice);

    const url = new URL(previewURL);

    const handleMessage = (e: MessageEvent) => {
        if (e.origin !== cfg.previewHost) {
            return;
        }

        try {
            const parsedData = JSON.parse(e.data);

            if (parsedData.event === DISPLAY_HIDDEN_QUESTIONS_TYPE) {
                setIsFrameTumblerOn(!parsedData.payload.displayHiddenQuestions);
            }

            // eslint-disable-next-line no-empty
        } catch (_) {}
    };

    useEffect(() => {
        window.addEventListener('message', handleMessage);

        return () => window.removeEventListener('message', handleMessage);
    }, []);

    if (withHeader) {
        url.searchParams.set('_header_enable_', '1');
    }

    if (data?.pageId) {
        url.searchParams.set('_page_id_', String(data.pageId));
    }

    const reloadIframe = () => {
        if (iframe.current) {
            const currentUrl = new URL(iframe.current.src);

            if (isFrameTumblerOn) {
                currentUrl.searchParams.set('_show_hidden_questions_', '1');
            } else {
                currentUrl.searchParams.delete('_show_hidden_questions_');
            }
            iframe.current.src = currentUrl.href;
        }
    };

    const handleDarkModeTumblerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsDarkModeTumblerOn(e.target.checked);
        iframe.current?.contentWindow?.postMessage(
            {
                event: SET_THEME_MODE_EVENT,
                mode: e.target.checked ? 'dark' : 'light'
            },
            url.origin
        );
    };

    return (
        <div className={`${b()} ${className}`}>
            <div className={b('header')}>
                <div className={b('device-select')}>
                    {!onlyPanel &&
                        PREVIEW_DEVICES.map(previewDevice => (
                            <button
                                key={previewDevice}
                                className={b('device-button', {
                                    type: previewDevice,
                                    active: previewDevice === selectedDevice ? 'yes' : 'no'
                                })}
                                onClick={() => setSelectedDevice(previewDevice)}
                            >
                                <img src={DEVICE_ICONS[previewDevice]} />
                            </button>
                        ))}
                </div>
            </div>
            {withHeader && (
                <div className={b('cap')}>
                    {!isFrameLoading && (
                        <Button
                            className={b('help')}
                            view='dl-clear'
                            size='s'
                            onClick={() => showMobileMessage({
                                title: i18n.mobileMessage.title,
                                description: i18n.mobileMessage.text,
                                level: 'PopupOverPopup'
                            })}
                            width='auto'
                            icon={(cls: string) => {
                                return <Icon className={cls} type='help-yellow' size='s' />;
                            }}
                        />
                    )}
                    <div className={b('preview-buttons')}>
                        <div className={b('left-buttons')}>
                            {device !== Device.Mobile && (
                                <>
                                    <ShareButton
                                        className={b('button', {shareBtn: true})}
                                        width='auto'
                                        link={previewURL}
                                    />
                                    <Button
                                        className={b('button', {reloadBtn: true})}
                                        view='dl-default'
                                        size='s'
                                        onClick={reloadIframe}
                                        width='auto'
                                    >
                                        <span>{i18n.restart}</span>
                                    </Button>
                                </>
                            )}
                        </div>
                        {supportDarkMode &&
                            <div className={b('tumbler')}>
                                <Tumbler
                                    labelBefore={i18n.darkMode}
                                    size='m'
                                    view='dl'
                                    checked={isDarkModeTumblerOn}
                                    onChange={handleDarkModeTumblerChange}
                                />
                            </div>
                        }
                        <div className={b('right-buttons')}>
                            {device === Device.Mobile ? null : (
                                <ModeButton className={b('mode-btn')} />
                            )}
                            <Button
                                className={b('cross-button')}
                                view='dl-default'
                                size='s'
                                onClick={() => onClose?.()}
                                width={device === Device.Mobile ? 'max' : 'auto'}
                                icon={(cls: string) => {
                                    return <Icon className={cls} type='cross' size='s' />;
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className={b('device', {type: selectedDevice})}>
                <iframe
                    className={b('iframe')}
                    ref={iframe}
                    src={url.href}
                    onLoad={() => setIsFrameLoading(false)}
                />
            </div>
        </div>
    );
}
